import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getCartArticles } from '../services/api';
import { BookingMode } from '../types/order-main';
import type { Article } from '../types/common';

const modesAnyProductOnly = [
  BookingMode.productOnly,
  BookingMode.productOnlyMph,
];

const modesAnyNe4Only = [
  BookingMode.ne4Only,
  BookingMode.ne4AndProduct,
];

const modesHouseConnection = [
  BookingMode.houseConnectionOnly,
  BookingMode.houseConnectionAndNe4,
  BookingMode.houseConnectionAndProduct,
  BookingMode.houseConnectionAndNe4AndProduct,
];

const modesEeccRelevant = [
  BookingMode.productOnly,
  BookingMode.productOnlyMph,
  BookingMode.ne4AndProduct,
  BookingMode.houseConnectionAndProduct,
  BookingMode.houseConnectionAndNe4AndProduct,
];

const useBookingStore = defineStore('booking', () => {
  const articles = ref<Article[]>([]);
  const mode = ref<BookingMode>();

  const currentBookingMode = computed<BookingMode | undefined>(() => mode.value);
  const isCurrentBookingModeAnyProductOnly = computed<boolean>(
    () => mode.value !== undefined && modesAnyProductOnly.includes(mode.value),
  );
  const isCurrentBookingModeAnyNe4Only = computed<boolean>(
    () => mode.value !== undefined && modesAnyNe4Only.includes(mode.value),
  );
  const isCurrentBookingModeAnyHouseConnection = computed<boolean>(
    () => mode.value !== undefined && modesHouseConnection.includes(mode.value),
  );
  const isCurrentBookingModeEeccRelevant = computed<boolean>(
    () => mode.value !== undefined && modesEeccRelevant.includes(mode.value),
  );
  const providers = computed<string[]>(
    () => articles.value.filter(({ term }) => term === 'Anbieter').map(({ value }) => value),
  );

  function getArticleByArticleNumber(articleNumber: string) {
    return articles.value.find(item => item.articleNumber === articleNumber);
  }

  function applyDiscountToArticle(articleNumber: string): Article | undefined {
    const target = articles.value.find(item => item.articleNumber === articleNumber);
    if (!target) {
      return undefined;
    }
    target.isDiscountApplied = true;
    return target;
  }

  function clearDiscounts(): Article[] {
    const candidates = articles.value.filter(article => !!article.isDiscountApplied);

    candidates.forEach(article => {
      article.isDiscountApplied = false;
    });

    return candidates;
  }

  function updateMode(targetMode: BookingMode) {
    mode.value = targetMode;
  }

  async function loadArticles(): Promise<void> {
    const [err, result] = await getCartArticles();
    if (!err) {
      articles.value = result;
    }
  }

  return {
    articles,
    currentBookingMode,
    isCurrentBookingModeAnyHouseConnection,
    isCurrentBookingModeAnyNe4Only,
    isCurrentBookingModeAnyProductOnly,
    isCurrentBookingModeEeccRelevant,
    providers,
    applyDiscountToArticle,
    clearDiscounts,
    getArticleByArticleNumber,
    updateMode,
    loadArticles,
  };
});

export default useBookingStore;
